import React from 'react'
import {
  Button,
  ModalInjectedProps,
  Tabs,
  Tab,
  TabPanel,
} from '@sketch/components'
import {
  WorkspaceMinimalFragment,
  useGetProjectMembershipsQuery,
  useGetWorkspaceMembershipsQuery,
  ProjectMembershipFragment,
  WorkspaceMembershipFragment,
  ProjectInSidebarAndHeaderFragment,
} from '@sketch/gql-types'
import { NewProjectSharingHeader } from './NewProjectSharingHeader'
import { WorkspacePanel } from './Workspace/WorkspacePanel'
import { GuestsPanel } from './Guests/GuestsPanel'
import { LeaveProjectButton } from './LeaveProjectButton'
import {
  StyledModal as Modal,
  StyledTabList as TabList,
  TabPanelWrapper,
} from 'modules/shares/modals/SharingModal/SharingModal.styles'

interface NewProjectSharingModalProps extends ModalInjectedProps {
  project: ProjectInSidebarAndHeaderFragment
  workspace?: WorkspaceMinimalFragment
}

export const NewProjectSharingModal: React.FC<NewProjectSharingModalProps> = ({
  project,
  workspace,
  hideModal,
}) => {
  const hasAccess = project.workspaceAccessLevel === 'DEFAULT'

  const { error, loading, data, fetchMore } = useGetProjectMembershipsQuery({
    variables: { projectIdentifier: project.identifier },
  })
  // used for the autocomplete and email validations
  const workspaceMembers = useGetWorkspaceMembershipsQuery({
    variables: { workspaceId: workspace?.identifier || '' },
    skip: !workspace,
  })

  const memberships =
    (data?.project.memberships.entries as ProjectMembershipFragment[]) ?? []
  const meta = data?.project.memberships?.meta

  // do not show workspace members on the guest panel
  const guests = memberships.filter(
    member => member.workspaceMembership.role === 'GUEST'
  )
  const members = memberships.filter(
    member => member.workspaceMembership.role !== 'GUEST'
  )

  if (!workspace) {
    return null
  }

  const workspaceMemberships =
    (workspaceMembers.data?.workspace.memberships.list
      ?.entries as WorkspaceMembershipFragment[]) ?? []

  return (
    <Modal onCancel={hideModal}>
      <NewProjectSharingHeader
        project={project}
        workspaceId={workspace.identifier}
        workspaceName={workspace.name}
      />
      <Modal.Body>
        <Tabs>
          <TabList shouldDrawSeparator>
            <Tab>Workspace Members ({hasAccess ? `All` : `Restricted`})</Tab>
            <Tab>
              Guests {error || loading || !meta ? null : `(${guests.length})`}
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelWrapper>
              <WorkspacePanel
                workspace={workspace}
                project={project}
                members={members}
                workspaceMemberships={workspaceMemberships}
                after={meta?.after}
                fetchMore={fetchMore}
              />
            </TabPanelWrapper>
          </TabPanel>
          <TabPanel>
            <TabPanelWrapper>
              <GuestsPanel
                workspace={workspace}
                project={project}
                guests={guests}
                workspaceMemberships={workspaceMemberships}
                after={meta?.after}
                fetchMore={fetchMore}
              />
            </TabPanelWrapper>
          </TabPanel>
        </Tabs>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          project.userCanLeave ? (
            <LeaveProjectButton project={project} />
          ) : undefined
        }
      >
        <Button onClick={hideModal}>Done</Button>
      </Modal.Footer>
    </Modal>
  )
}
